import { useEffect, useState, useMemo } from 'react';
import { Loader2, X, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';
import { usePicklistStore } from '../store';
import { picklistService } from '../services';
import type { PicklistPickerDto } from '../types';

interface ManagePickersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ManagePickersDialog({ open, onOpenChange }: ManagePickersDialogProps) {
  const { selectedPicklist, addPicker, removePicker, isManagingPickers } = usePicklistStore();

  const [eligibleUsers, setEligibleUsers] = useState<PicklistPickerDto[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  // Fetch eligible pickers when dialog opens
  useEffect(() => {
    if (open && eligibleUsers.length === 0) {
      setIsLoadingUsers(true);
      picklistService
        .getEligiblePickers()
        .then((data) => setEligibleUsers(data))
        .catch(() => toast.error('Failed to load eligible pickers'))
        .finally(() => setIsLoadingUsers(false));
    }
  }, [open, eligibleUsers.length]);

  const pickers: PicklistPickerDto[] = selectedPicklist?.pickers ?? [];

  // Filter out users already assigned as pickers
  const availableUsers = useMemo(() => {
    const pickerUserIds = new Set(pickers.map((p) => p.userId));
    return eligibleUsers.filter((u) => !pickerUserIds.has(u.userId));
  }, [eligibleUsers, pickers]);

  const handleAddPicker = async () => {
    if (!selectedUserId) return;

    try {
      await addPicker(Number(selectedUserId));
      const user = eligibleUsers.find((u) => u.userId === Number(selectedUserId));
      toast.success(`Added ${user?.fullName ?? 'user'} as picker`);
      setSelectedUserId('');
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to add picker');
      toast.error('Failed to add picker', { description: message });
    }
  };

  const handleRemovePicker = async (picker: PicklistPickerDto) => {
    if (pickers.length <= 1) {
      toast.error('Cannot remove the last picker');
      return;
    }

    try {
      await removePicker(picker.userId);
      toast.success(`Removed ${picker.fullName} from pickers`);
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to remove picker');
      toast.error('Failed to remove picker', { description: message });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Pickers</DialogTitle>
          <DialogDescription>
            Add or remove users assigned to pick items for this picklist.
            Only users with the fulfill picklist permission are shown.
          </DialogDescription>
        </DialogHeader>

        {/* Add picker section */}
        <div className="flex gap-2">
          <Select
            value={selectedUserId}
            onValueChange={setSelectedUserId}
            disabled={isLoadingUsers || isManagingPickers}
          >
            <SelectTrigger className="flex-1">
              <SelectValue
                placeholder={isLoadingUsers ? 'Loading users...' : 'Select a user'}
              />
            </SelectTrigger>
            <SelectContent>
              {availableUsers.map((user) => (
                <SelectItem key={user.userId} value={user.userId.toString()}>
                  {user.fullName}
                </SelectItem>
              ))}
              {availableUsers.length === 0 && !isLoadingUsers && (
                <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                  No available users
                </div>
              )}
            </SelectContent>
          </Select>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={handleAddPicker}
                  disabled={!selectedUserId || isManagingPickers}
                  size="icon"
                  aria-label="Add picker"
                >
                  {isManagingPickers ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <UserPlus className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Add picker</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Current pickers list */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground">
            Assigned Pickers ({pickers.length})
          </p>
          {pickers.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No pickers assigned
            </p>
          ) : (
            <div className="space-y-1">
              {pickers.map((picker) => (
                <div
                  key={picker.id}
                  className="flex items-center justify-between rounded-md border px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{picker.fullName}</span>
                    {picker.userId === selectedPicklist?.createdById && (
                      <Badge variant="secondary" className="text-xs">
                        Creator
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => handleRemovePicker(picker)}
                    disabled={isManagingPickers || pickers.length <= 1}
                    title={
                      pickers.length <= 1
                        ? 'Cannot remove the last picker'
                        : `Remove ${picker.fullName}`
                    }
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
